import React, { useCallback, useMemo, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ChevronRight, CalendarPlus, GraduationCap, Wallet as WalletIcon, Sparkles, BookOpen } from 'lucide-react';
import { getProAcademy, downloadProAgenda } from '../services/professionalAcademyApi';
import { zoekProAcademy } from '../services/proAcademyApi';
import { leesEigenPostcode } from '../utils/pc4Afstand';
import type { ProActivatie, ProTraining, ProWallet } from '../services/professionalAcademyApi';
import { ProAcademyRij } from '../components/proAcademy/ProAcademyRij';
import { ProAcademyDetailModal } from '../components/proAcademy/ProAcademyDetailModal';
import { ProAcademyActieveFilters, ProAcademyFilterbalk } from '../components/proAcademy/ProAcademyFilterbalk';
import { ProAcademyZoekGrid } from '../components/proAcademy/ProAcademyZoekGrid';
import { filtersUitParams, heeftFilters, paramsUitFilters, LEGE_FILTERS, type ProFilters } from '../components/proAcademy/proAcademyFilters';
import { datumLang, eur, pePunten } from '../components/proAcademy/proAcademyStijl';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

function datumTijd(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Compacte wallet-strook: fictief PE-budget + geregistreerde PE-punten van dit kalenderjaar. */
const WalletStrook: React.FC<{ wallet: ProWallet; discipline: string | null }> = ({ wallet, discipline }) => {
  const { t } = useTranslation('pages');
  const pct = wallet.budget_totaal > 0 ? Math.min(100, Math.round((wallet.budget_resterend / wallet.budget_totaal) * 100)) : 0;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="rounded-lg bg-emerald-50 p-2 text-emerald-700">
          <WalletIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('proAcademy.walletTitel', { defaultValue: 'Uw PE-budget {{jaar}}', jaar: wallet.boekjaar })}</p>
          <p className="text-xs text-gray-500">
            {discipline
              ? t('proAcademy.walletDiscipline', { defaultValue: '{{discipline}} · {{pct}}% BeLagom-korting automatisch toegepast', discipline, pct: wallet.korting_pct })
              : t('proAcademy.walletKorting', { defaultValue: '{{pct}}% BeLagom-korting wordt automatisch toegepast.', pct: wallet.korting_pct })}
          </p>
        </div>
      </div>
      <div className="min-w-[10rem] flex-1">
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className="text-gray-500">{t('proAcademy.walletResterend', { defaultValue: 'Resterend (fictief budget)' })}</span>
          <span className="font-semibold text-gray-900">
            {eur.format(wallet.budget_resterend)} <span className="font-normal text-gray-400">/ {eur.format(wallet.budget_totaal)}</span>
          </span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
        <GraduationCap className="h-5 w-5 text-amber-600" aria-hidden="true" />
        <div className="text-xs">
          <p className="font-semibold text-gray-900">{t('proAcademy.walletPe', { defaultValue: '{{n}} PE-punten', n: pePunten(wallet.pe_punten_jaar) })}</p>
          <p className="text-gray-500">{t('proAcademy.peGeregistreerd', { defaultValue: 'geregistreerd in {{jaar}}', jaar: wallet.boekjaar })}</p>
        </div>
      </div>
    </div>
  );
};

const ActivatieStatus: React.FC<{ status: ProActivatie['status'] }> = ({ status }) => {
  const { t } = useTranslation('pages');
  const config = {
    aangemeld: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: t('proAcademy.statusAangemeld', { defaultValue: 'Aangemeld' }) },
    bevestigd: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: t('proAcademy.statusBevestigd', { defaultValue: 'Geactiveerd' }) },
    betaald: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: t('proAcademy.statusBetaald', { defaultValue: 'Betaald' }) },
    geannuleerd: { cls: 'bg-gray-100 text-gray-500 border-gray-200', label: t('proAcademy.statusGeannuleerd', { defaultValue: 'Geannuleerd' }) },
  }[status] ?? { cls: 'bg-gray-100 text-gray-500 border-gray-200', label: status };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${config.cls}`}>{config.label}</span>;
};

/**
 * Professional Academy — het expertportaal in Netflix-stijl: lichte BeLagom-huisstijl met filters in de linkerzijbalk,
 * 6 oneindig scrolbare categorie-rijen (de eigen discipline bovenaan), cinemakaarten met
 * accreditatie-badge en hover-gloed, en het Informatie-scherm met de financiële breakdown.
 */
export const ProfessionalAcademyPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [searchParams, setSearchParams] = useSearchParams();
  // Filter-, sorteer- & zoekengine (spec §3): status in de URL; eigen postcode alleen lokaal.
  const filters = useMemo(() => filtersUitParams(searchParams), [searchParams]);
  // react-router v7 verwerkt navigaties in een transition: pas alleen de door de aanroeper
  // gewijzigde sleutels (t.o.v. de laatst gerenderde filters) toe op de actuele URL, zodat een
  // snelle tweede klik of timer een zojuist gezette parameter niet overschrijft.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const zetFilters = useCallback(
    (next: ProFilters) =>
      setSearchParams((prev) => {
        const actueel = filtersUitParams(prev);
        const gezien = filtersRef.current;
        const merged: ProFilters = { ...actueel };
        for (const key of Object.keys(next) as Array<keyof ProFilters>) {
          if (JSON.stringify(next[key]) !== JSON.stringify(gezien[key])) (merged as unknown as Record<string, unknown>)[key] = next[key];
        }
        return paramsUitFilters(merged);
      }, { replace: true }),
    [setSearchParams]
  );
  const [eigenPostcode, setEigenPostcode] = useState<string | null>(() => leesEigenPostcode());
  const [open, setOpen] = useState<ProTraining | null>(null);

  // Heractivatie: nieuwe startdata van opleiders verschijnen vanzelf weer in de rijen.
  const academyQuery = useQuery({ queryKey: ['pro-academy'], queryFn: getProAcademy, refetchInterval: 60_000 });
  const filterActief = heeftFilters(filters);
  const zoekParams = useMemo(
    () => ({
      vak: filters.vak ?? undefined, map: filters.map ?? undefined, q: filters.q ?? undefined, reg: filters.reg, pe: filters.pe,
      vorm: filters.vorm ?? undefined, examen: filters.examen, dag: filters.dag, hobby: filters.hobby, alles: filters.alles, sort: filters.sort,
    }),
    [filters]
  );
  const trainingenQuery = useQuery({
    queryKey: ['pro-academy-zoek', zoekParams],
    queryFn: () => zoekProAcademy(zoekParams),
    enabled: filterActief,
    placeholderData: (vorige) => vorige,
  });
  const agendaMutation = useMutation({ mutationFn: (a: ProActivatie) => downloadProAgenda(a.id, a.titel) });

  const data = academyQuery.data;
  const hoofdmappen = useMemo(() => data?.hoofdmappen ?? [], [data?.hoofdmappen]);
  const rijen = data?.rijen ?? [];
  const activaties = data?.activaties ?? [];
  const wallet = data?.wallet;

  const hoofdmap = filters.map;
  const kiesHoofdmap = (key: ProFilters['map']) => zetFilters({ ...filters, map: hoofdmap === key ? null : key, alles: false });
  const wisAlles = () => zetFilters({ ...LEGE_FILTERS, hobby: filters.hobby, sort: filters.sort });

  const zoekResultaat = trainingenQuery.data?.data;
  const actieveMap = hoofdmappen.find((h) => h.key === (zoekResultaat?.filters.hoofdmap_effectief ?? hoofdmap)) ?? null;
  const resultTitel = zoekResultaat?.vakgebied
    ? `${zoekResultaat.vakgebied.label}${actieveMap ? ` · ${actieveMap.emoji} ${actieveMap.label}` : ''}`
    : actieveMap
      ? `${actieveMap.emoji} ${actieveMap.label}`
      : filters.q
        ? t('proAcademy.resultatenVoor', { defaultValue: 'Resultaten voor "{{zoek}}"', zoek: filters.q })
        : filters.alles
          ? t('proAcademy.alleCursussen', { defaultValue: 'Alle cursussen' })
          : t('proAcademy.gefilterd', { defaultValue: 'Gefilterde opleidingen' });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">{t('proAcademy.eyebrow', { defaultValue: 'Expertportaal' })}</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">{t('proAcademy.titel', { defaultValue: 'Professional Academy' })}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('proAcademy.subtitel', { defaultValue: 'Geaccrediteerde bijscholing en workshops — minimaal 5% voordeliger dan rechtstreeks bij de aanbieder.' })}
          </p>
        </div>
        <Link to="/mijn-opleidingen" className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-400">
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          {t('proAcademy.mijnOpleidingen', { defaultValue: 'Mijn opleidingen & facturen' })}
        </Link>
      </header>

      {academyQuery.isError && (
        <p className="text-sm text-red-700" role="alert">
          {foutMelding(academyQuery.error, t('proAcademy.laadfout', { defaultValue: 'De Professional Academy kon niet worden geladen.' }))}
        </p>
      )}
      {wallet && <WalletStrook wallet={wallet} discipline={data?.discipline.label ?? null} />}

      <div className="lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-8">
        {/* Linkerzijbalk: vakgebied-zoeker, sortering, hoofdmappen, PE-registers, leervorm, weekdagen, reistijd, hobby */}
        <aside className="mb-6 lg:sticky lg:top-4 lg:mb-0 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto">
          <ProAcademyFilterbalk
            filters={filters}
            vakgebied={zoekResultaat?.vakgebied ?? null}
            meta={trainingenQuery.data?.meta}
            hoofdmappen={hoofdmappen}
            eigenPostcode={eigenPostcode}
            onEigenPostcode={setEigenPostcode}
            onChange={zetFilters}
          >
            {/* De 6 hoofdmappen (rijkleur), eigen discipline gemarkeerd */}
            {hoofdmappen.length > 0 && (
              <fieldset className="space-y-2">
                <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t('proAcademy.hoofdmappen', { defaultValue: 'Hoofdmappen' })}</legend>
                <div className="flex flex-col gap-1">
                  {hoofdmappen.map((h) => {
                    const actief = hoofdmap === h.key;
                    const eigen = data?.discipline.hoofdmap === h.key;
                    return (
                      <button
                        key={h.key}
                        type="button"
                        onClick={() => kiesHoofdmap(h.key)}
                        aria-pressed={actief}
                        className={`flex w-full items-center gap-2 rounded-lg border-l-4 px-3 py-2 text-left text-sm font-medium transition ${
                          actief ? 'bg-[#0B1F3A] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        style={{ borderLeftColor: h.kleur }}
                      >
                        <span aria-hidden="true">{h.emoji}</span>
                        <span className="min-w-0 flex-1 truncate">{h.label}</span>
                        {eigen && <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: actief ? '#FFFFFF' : h.kleur }} aria-label={t('proAcademy.rij.eigenDiscipline', { defaultValue: 'Uw vakgebied' })} />}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}
          </ProAcademyFilterbalk>
        </aside>

        {/* Hoofdworkspace: actieve filterbadges, dan grid of Netflix-rijen */}
        <div className="min-w-0 space-y-6">
          <ProAcademyActieveFilters filters={filters} vakgebied={zoekResultaat?.vakgebied ?? null} meta={trainingenQuery.data?.meta} hoofdmappen={hoofdmappen} onChange={zetFilters} />
          {filterActief ? (
            <>
              {trainingenQuery.isLoading && <p className="text-sm text-gray-500">…</p>}
              {trainingenQuery.isError && (
                <p className="text-sm text-red-700" role="alert">
                  {foutMelding(trainingenQuery.error, t('proAcademy.trainingenFout', { defaultValue: 'Cursussen konden niet worden geladen.' }))}
                </p>
              )}
              {zoekResultaat && (
                <ProAcademyZoekGrid
                  trainingen={zoekResultaat.trainingen}
                  filters={filters}
                  eigenPostcode={eigenPostcode}
                  afgekapt={zoekResultaat.afgekapt}
                  titel={resultTitel}
                  onOpen={setOpen}
                  onWis={wisAlles}
                  onZaterdagUit={() => zetFilters({ ...filters, dag: filters.dag.filter((d) => d !== 6) })}
                  onNaarGroei={() => zetFilters({ ...LEGE_FILTERS, hobby: filters.hobby, sort: filters.sort, map: 'ld' })}
                />
              )}
            </>
          ) : (
            /* De categorie-rijen (swipers), discipline-gesorteerd */
            <div className="space-y-8">
              {academyQuery.isLoading && (
                <div className="flex gap-4 overflow-hidden" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="aspect-[16/12] w-[20rem] shrink-0 animate-pulse rounded-xl bg-white" />
                  ))}
                </div>
              )}
              {academyQuery.isSuccess && rijen.length === 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <p className="text-sm text-gray-600">{t('proAcademy.catalogusLeeg', { defaultValue: 'De catalogus wordt nog gevuld. Kom binnenkort terug!' })}</p>
                </div>
              )}
              {rijen.map((rij) => (
                <ProAcademyRij
                  key={rij.key}
                  id={rij.key}
                  titel={rij.label}
                  subtitel={rij.subtitel}
                  emoji={rij.emoji}
                  accent={rij.kleur}
                  eigen={rij.eigen}
                  waarschuwing={!rij.pe}
                  trainingen={rij.trainingen}
                  onOpen={setOpen}
                />
              ))}
              {rijen.length > 0 && (
                <button
                  type="button"
                  onClick={() => zetFilters({ ...filters, alles: true })}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 hover:border-gray-400"
                >
                  {t('proAcademy.alleCursussenBekijken', { defaultValue: 'Alle cursussen van A tot Z bekijken' })}
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <section>
        <h2 className="mb-3 text-lg font-bold text-gray-900">{t('proAcademy.mijnActivaties', { defaultValue: 'Mijn activaties' })}</h2>
        {activaties.length === 0 ? (
          <p className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
            {t('proAcademy.geenActivaties', { defaultValue: 'U heeft nog geen cursus geactiveerd via het portaal.' })}
          </p>
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {activaties.map((a) => (
              <li key={a.id} className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{a.titel}</p>
                    <p className="text-xs text-gray-500">
                      {a.hoofdmap_emoji} {a.hoofdmap_label} · {a.organisatie_naam}
                      {a.sessie_start
                        ? ` · ${t('proAcademy.startOp', { defaultValue: 'start {{datum}}', datum: datumLang(a.sessie_start) })}${
                            a.aantal_lessen > 1 ? ` · ${t('proAcademy.aantalLessen', { defaultValue: '{{n}} lessen', n: a.aantal_lessen })}` : ''
                          }`
                        : (a.datum_tijd || a.wanneer) && ` · ${a.datum_tijd ? datumTijd(a.datum_tijd) : a.wanneer}`}
                    </p>
                  </div>
                  <ActivatieStatus status={a.status} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                  <span>
                    {t('proAcademy.netto', { defaultValue: 'Netto' })}: <strong className="text-gray-900">{eur.format(a.netto_bedrag)}</strong>
                  </span>
                  {a.korting_bedrag > 0 && (
                    <span>
                      {t('proAcademy.bespaard', { defaultValue: 'Bespaard' })}: {eur.format(a.korting_bedrag)}
                    </span>
                  )}
                  {a.pe_punten !== null && (
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <GraduationCap className="h-3.5 w-3.5" aria-hidden="true" />
                      {pePunten(a.pe_punten)} PE{a.pe_register ? ` · ${a.pe_register}` : ''}
                    </span>
                  )}
                </div>
                {a.heeft_agenda && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => agendaMutation.mutate(a)}
                      disabled={agendaMutation.isPending}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:text-sky-900 disabled:opacity-50"
                    >
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      {t('proAcademy.inAgenda', { defaultValue: 'Zet in mijn agenda (.ics)' })}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {agendaMutation.isError && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {t('proAcademy.detail.agendaFout', { defaultValue: 'Het agenda-bestand kon niet worden gedownload.' })}
          </p>
        )}
      </section>

      {open && <ProAcademyDetailModal training={open} wallet={wallet} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default ProfessionalAcademyPage;
